/**
 * publishService — single wrapper over the publisher/scheduler endpoints,
 * so the launch panel's one confirm button replaces the three publish
 * modals. Routing:
 *
 *   paid    + launch now  → POST /api/v1/publisher/publish-multi
 *   organic + launch now  → POST /api/v1/publisher/publish-organic
 *   any     + scheduled   → POST /api/v1/scheduler/schedule
 *
 * Payload shapes mirror MultiAccountPublishModal / OrganicPostModal /
 * SchedulePublishModal exactly — the backend endpoints are unchanged.
 */

import { apiClient } from '@/services/api';
import { conceptPublishAssetUrl } from './conceptArt';
import type { CreativeConcept } from './types';

/** A finished ad's own file per Meta placement, when it has both (else null: one file ships). */
export function bakedPlacementFiles(concept: CreativeConcept): { feed: string; story: string } | null {
  const file = (fmt: string) =>
    concept.renditions?.find((r) => r.platform === 'meta' && r.format === fmt && r.enabled !== false && r.assetUrl)?.assetUrl;
  const feed = file('feed_1x1');
  const story = file('story_9x16');
  return feed && story ? { feed, story } : null;
}

// Currency symbols the LLM or canvas state may store instead of ISO codes.
const CURRENCY_SYMBOL_TO_CODE: Record<string, string> = {
  '₹': 'INR', '$': 'USD', '£': 'GBP', '€': 'EUR',
  'د.إ': 'AED', '¥': 'JPY', '₩': 'KRW', 'A$': 'AUD', 'C$': 'CAD', 'S$': 'SGD',
};

function normalizeCurrencyCode(val: string | undefined): string {
  if (!val) return 'USD';
  const trimmed = val.trim();
  if (/^[A-Za-z]{3}$/.test(trimmed)) return trimmed.toUpperCase();
  return CURRENCY_SYMBOL_TO_CODE[trimmed] ?? 'USD';
}

/** FastAPI returns detail as either a string or an array of validation-error objects. */
function extractErrorMessage(e: any, fallback = 'Request failed'): string {
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg ?? JSON.stringify(d)).join('; ');
  return String(detail);
}

/**
 * Derive Meta placement lists from canvas concept renditions.
 * Returns null when no rendition data is present so callers can leave
 * compiler-set placement untouched.
 *
 * Rendition formats → Meta API positions:
 *   feed_1x1   → facebook: feed,  instagram: stream
 *   story_9x16 → facebook: story, instagram: story
 */
function metaPlacementsFromRenditions(concepts: CreativeConcept[]): {
  facebook_positions: string[];
  instagram_positions: string[];
} | null {
  const metaRenditions = concepts
    .filter((c) => c.included !== false)
    .flatMap((c) => c.renditions ?? [])
    .filter((r) => r.platform === 'meta' && r.enabled !== false);

  if (!metaRenditions.length) return null;

  const hasFeed = metaRenditions.some((r) => r.format === 'feed_1x1');
  const hasStory = metaRenditions.some((r) => r.format === 'story_9x16');

  const fb: string[] = [];
  const ig: string[] = [];
  if (hasFeed) { fb.push('feed'); ig.push('stream'); }
  if (hasStory) { fb.push('story'); ig.push('story'); }

  return fb.length ? { facebook_positions: fb, instagram_positions: ig } : null;
}

/**
 * Apply canvas rendition-derived placements to a compiled campaign config.
 * Only modifies configs produced by the campaign compiler (source: "campaign_compiler")
 * because those are the ones that hard-code placement posture without consulting
 * the canvas rendition state. Legacy/LLM-proposal configs are left unchanged.
 */
function applyRenditionPlacements(config: any, concepts: CreativeConcept[]): void {
  if (config?.source !== 'campaign_compiler') return;
  const placement = metaPlacementsFromRenditions(concepts);
  if (!placement) return;
  for (const adSet of (config.ad_sets ?? [])) {
    if (adSet.targeting) {
      adSet.targeting.facebook_positions = placement.facebook_positions;
      adSet.targeting.instagram_positions = placement.instagram_positions;
    }
  }
}

/**
 * Build a minimal campaign_config from canvas state so the organic publisher
 * can extract ad creatives. Used when the conversation has no stored
 * campaign_config (canvas-native flow never populates it via the old proposal
 * path).
 *
 * Produces the legacy platform-keyed format that extract_platform_config
 * understands: { meta: { ad_sets: [{ ads: [...] }] }, pinterest: {...}, ... }
 */
function buildCampaignConfigFromState(
  campaignState: CampaignState,
  accounts: Array<{ platform: string; enabled?: boolean }>,
  selectedConceptIds: string[],
  uploadedImages?: Array<{ url: string }>,
): Record<string, any> {
  const platformKeys = [
    ...new Set(
      accounts
        .filter((a) => a.enabled)
        .map((a) => (['facebook', 'instagram'].includes(a.platform) ? 'meta' : a.platform)),
    ),
  ];

  const allConcepts = campaignState.creativeSet ?? [];
  const concepts = allConcepts.filter(
    (c) =>
      c.included !== false &&
      (selectedConceptIds.length === 0 || selectedConceptIds.includes(c.id)),
  );

  /**
   * The concept's image, resolved exactly as the canvas previews resolve it.
   *
   * The positional uploaded-image fallback MUST be indexed over the full creativeSet,
   * not this filtered list: CreativePanel renders previews with the index of the
   * unfiltered set, so indexing the filtered one would attach a different uploaded
   * image than the user saw whenever any concept is excluded or deselected.
   */
  const assetFor = (concept: CreativeConcept): string | undefined => {
    const canvasIndex = allConcepts.findIndex((c) => c.id === concept.id);
    return conceptPublishAssetUrl(
      concept, canvasIndex >= 0 ? canvasIndex : 0, uploadedImages,
    );
  };

  const sharedMeta = campaignState.messaging?.meta;

  const config: Record<string, any> = {};
  for (const key of platformKeys) {
    // Google Ads uses RSA format (headlines[]/descriptions[] arrays + keywords).
    // All other platforms use the canvas per-concept ad format.
    let ads: Record<string, any>[];
    if (key === 'google_ads' || key === 'google') {
      const gMsg = campaignState.messaging?.google;
      const fallbackHeadline = sharedMeta?.headline ?? campaignState.name ?? 'Ad';
      const fallbackBody = sharedMeta?.body ?? '';
      const rsaAd: Record<string, any> = {
        headlines: gMsg?.headlines?.length ? gMsg.headlines : [fallbackHeadline],
        descriptions: gMsg?.descriptions?.length ? gMsg.descriptions : (fallbackBody ? [fallbackBody] : ['Learn more']),
      };
      // Same canvas-identical asset resolution as the Meta branch below.
      const firstAsset = concepts[0] ? assetFor(concepts[0]) : undefined;
      if (firstAsset) rsaAd.image_url = firstAsset;
      // Image POOL for Responsive Display / Performance Max asset groups: Google
      // auto-crops and recombines these with the headlines/descriptions.
      const imagePool = concepts
        .map((c) => assetFor(c))
        .filter(Boolean) as string[];
      if (imagePool.length) rsaAd.image_urls = imagePool;
      ads = [rsaAd];
    } else if (key === 'meta') {
      // META creative model. Each plain-image concept becomes one "placement-optimized"
      // dynamic ad: its tall master is cropped per placement (feed 4:5 / story 9:16) at
      // the saved position via Meta asset_customization_rules, and the copy POOL
      // (headlines × bodies) is combined by Meta. Concept-level A/B happens via ad
      // rotation across these ads. Carousels and finished ads (baked copy) ship as their
      // own standalone ads (they can't be recombined/re-cropped).
      const headlinePool = Array.from(new Set([sharedMeta?.headline, ...(sharedMeta?.headlines ?? [])].filter(Boolean))) as string[];
      const bodyPool = Array.from(new Set([sharedMeta?.body, ...(sharedMeta?.bodies ?? [])].filter(Boolean))) as string[];
      const metaAds: Record<string, any>[] = [];
      for (const concept of concepts) {
        // Resolve the master image exactly the way the canvas previews do — see
        // conceptPublishAssetUrl. Reading variations[selectedVariation] ?? variations[0]
        // directly (as this did) silently produced imageless ads for concepts whose
        // asset lives on another variation, on a rendition, or in the conversation's
        // uploaded images, all of which the visualizers display.
        const assetUrl = assetFor(concept);
        const conceptMetaCopy = concept.copy?.meta;
        const headline = conceptMetaCopy?.headline ?? sharedMeta?.headline ?? campaignState.name ?? '';
        const primaryText = conceptMetaCopy?.body ?? sharedMeta?.body ?? '';
        const cards = (concept.cards ?? []).filter((c) => c.assetUrl);

        if (cards.length >= 2) {
          metaAds.push({
            concept_id: concept.id, headline, primary_text: primaryText,
            description: concept.concept ?? '', format: 'carousel',
            cards: cards.map((c) => ({ image_url: c.assetUrl, headline: c.headline ?? headline, link: c.link })),
          });
        } else if (concept.bakedCopy && concept.mediaType !== 'video' && bakedPlacementFiles(concept)) {
          // Finished ad made in several sizes (Ad Studio: 1:1 feed + 9:16 story) → one ad
          // that shows each placement its own file via the publisher's placement rules;
          // the files are uploaded as they are (no crop) and the copy is not recombined.
          const files = bakedPlacementFiles(concept)!;
          metaAds.push({
            concept_id: concept.id, format: 'placement_optimized',
            image_url: files.feed, placement_images: files,
            headline, primary_text: primaryText, description: concept.concept ?? '',
            headlines: [headline].filter(Boolean), bodies: [primaryText].filter(Boolean),
          });
        } else if (concept.bakedCopy || concept.mediaType === 'video') {
          // Finished ad / user video → standalone single-asset ad (no recrop/recombine).
          const ad: Record<string, any> = {
            concept_id: concept.id, headline, primary_text: primaryText, description: concept.concept ?? '',
          };
          if (assetUrl) ad.image_url = assetUrl;
          if (concept.mediaType === 'video') ad.media_type = 'video';
          metaAds.push(ad);
        } else if (assetUrl) {
          // Plain image → placement-optimized ad: publisher crops the master per
          // placement (using concept.adjustments) + combines the copy pool.
          const perConceptHeadlines = Array.from(new Set([headline, ...(conceptMetaCopy?.headlines ?? []), ...headlinePool].filter(Boolean))) as string[];
          const perConceptBodies = Array.from(new Set([primaryText, ...(conceptMetaCopy?.bodies ?? []), ...bodyPool].filter(Boolean))) as string[];
          metaAds.push({
            concept_id: concept.id,
            format: 'placement_optimized',
            image_url: assetUrl,
            adjustments: concept.adjustments ?? undefined,
            headline,
            primary_text: primaryText,
            description: concept.concept ?? '',
            headlines: perConceptHeadlines,
            bodies: perConceptBodies,
          });
        }
      }
      ads = metaAds;
    } else {
      // Pinterest / Twitter / TikTok — one ad per concept (no dynamic combination API).
      ads = concepts.map((concept) => {
        // Same canvas-identical asset resolution as the Meta/Google branches.
        const assetUrl = assetFor(concept);
        const conceptPlatformCopy = (concept.copy as any)?.[key];
        const conceptMetaCopy = concept.copy?.meta;
        const headline =
          conceptPlatformCopy?.headline ?? conceptPlatformCopy?.title ??
          conceptMetaCopy?.headline ?? sharedMeta?.headline ?? campaignState.name ?? '';
        const primaryText =
          conceptPlatformCopy?.body ?? conceptPlatformCopy?.description ??
          conceptPlatformCopy?.script ?? conceptPlatformCopy?.tweet ??
          conceptMetaCopy?.body ?? sharedMeta?.body ?? '';
        const ad: Record<string, any> = {
          concept_id: concept.id, headline, primary_text: primaryText, description: concept.concept ?? '',
        };
        if (assetUrl) {
          ad.image_url = assetUrl;
          if (concept.mediaType === 'video') ad.media_type = 'video';
          // Single-aspect crop hint for platforms with no crop API: Pinterest 2:3,
          // Twitter 1:1. The publisher crops the tall master to this aspect at publish.
          ad.target_aspect = key === 'pinterest' ? '2:3' : key === 'twitter' ? '1:1' : undefined;
          if (concept.adjustments) ad.adjustments = concept.adjustments;
        }
        return ad;
      });
    }

    const adSet: Record<string, any> = { name: campaignState.name ?? 'Campaign', ads };

    // For Google Ads: seed keywords from strategy_intent interests_semantic.
    if (key === 'google_ads' || key === 'google') {
      const siCampaigns = (campaignState as any).strategy_intent?.campaigns ?? [];
      const gIntent = siCampaigns.find(
        (c: any) => c.platform === 'google_ads' || c.platform === 'google',
      );
      const interests: string[] = gIntent?.ad_sets?.[0]?.audience_intent?.interests_semantic ?? [];
      if (interests.length) adSet.keywords = interests;
    }
    // Merge audience targeting from canvas state so _canonical_to_intent can
    // pick up interests, geo, etc. for campaigns without strategy_intent in state.
    const audienceTargeting = (campaignState.audience as any)?.targeting;
    if (audienceTargeting && typeof audienceTargeting === 'object') {
      // Normalize canvas geo format { geo: { type, centers } } → flat { locations, location_type }
      // that _geo_from_canonical in bridge.py expects.
      const normalized: Record<string, any> = { ...audienceTargeting };
      const geo = (audienceTargeting as any).geo;
      if (geo && typeof geo === 'object') {
        if (Array.isArray(geo.centers) && geo.centers.length) {
          normalized.locations = geo.centers;
        }
        if (geo.type) {
          // compiler's _geo_from_canonical maps "state" → "region"; canvas uses "region"
          normalized.location_type = geo.type;
        }
        delete normalized.geo;
      }
      adSet.targeting = normalized;
    }
    // For Meta, layer placement on top (rendition-derived placements take precedence).
    if (key === 'meta') {
      const placement = metaPlacementsFromRenditions(concepts);
      if (placement) adSet.targeting = { ...(adSet.targeting ?? {}), ...placement };
    }

    const platformCfg: Record<string, any> = {
      campaign: {
        name: campaignState.name ?? 'Campaign',
        objective: campaignState.objective ?? 'OUTCOME_TRAFFIC',
      },
      ad_sets: [adSet],
    };

    // Per-platform delivery mode. The compiler reads campaign_type on each platform
    // config and applies that platform's Advantage+ guardrails (Meta ASC / Google PMax)
    // — independent per platform, so Meta Advantage+ doesn't force Google PMax.
    const ctMap = campaignState.campaignType ?? {};
    const ct = ctMap[key] ?? (key === 'google_ads' ? ctMap['google'] : key === 'google' ? ctMap['google_ads'] : undefined);
    if (ct && ct !== 'manual') {
      platformCfg.campaign_type = ct;
    }

    // Carry budget so the compiler uses the canvas-set figure, not the brand-kit fallback.
    // budget.split[key] is a percentage; convert to per-day major-unit amount.
    const bgt = campaignState.budget;
    if (bgt?.total && bgt?.durationDays) {
      const splitKey = key === 'meta' ? 'meta' : key;
      const pct = bgt.split?.[splitKey] ?? (Object.keys(bgt.split ?? {}).length === 0 ? 100 : null);
      if (pct !== null) {
        platformCfg.daily_budget = (bgt.total * (pct / 100)) / bgt.durationDays;
        platformCfg.duration_days = bgt.durationDays;
        platformCfg.currency = normalizeCurrencyCode(bgt.currency);
      }
    }

    config[key] = platformCfg;
  }

  // strategy_intent is stored at top level (not under a platform key) so the
  // compiler's _normalize_to_canonical → _extract_intent can find it.
  const si = (campaignState as any).strategy_intent;
  if (si && typeof si === 'object') {
    config.strategy_intent = si;
  }

  return config;
}
import { OAuthApiClient } from '@/lib/oauth/OAuthApiClient';
import type { EnhancedOAuthConnection } from '@/lib/oauth/types';
import type { CampaignState } from './types';

export interface PublishAccount {
  connectionId: string;
  platform: string;
  accountId: string;
  accountName: string;
  enabled: boolean;
  isDefault?: boolean;
  /** Meta organic page / Pinterest board sub-targets, when chosen */
  pageId?: string;
  pageName?: string;
  boardId?: string;
}

export interface PublishOutcome {
  ok: boolean;
  scheduled: boolean;
  /** Conversation status implied by the results (publish-now only) */
  status?: 'published' | 'partially_published' | 'publish_failed';
  results: any[];
  error?: string;
}

/** Campaign split keys → OAuth connection platform names. */
const PLATFORM_ALIASES: Record<string, string[]> = {
  google: ['google_ads', 'google'],
  meta: ['meta', 'facebook'],
};

function matchesPlatform(connectionPlatform: string, campaignPlatform: string): boolean {
  const aliases = PLATFORM_ALIASES[campaignPlatform] ?? [campaignPlatform];
  return aliases.includes(connectionPlatform);
}

/**
 * Load active OAuth connections for the campaign's platforms, mapped to
 * launch-panel account rows. Defaults are pre-enabled, matching the old
 * modal's pre-selection behavior.
 */
export async function loadPublishAccounts(
  campaignPlatforms: string[],
  currentOrgId?: string,
): Promise<PublishAccount[]> {
  const oauthClient = new OAuthApiClient();
  const response = await oauthClient.getConnectionsForAllOrganizations();
  const byOrg = response?.connections_by_org ?? {};
  let connections: EnhancedOAuthConnection[] =
    (currentOrgId && byOrg[currentOrgId]) || Object.values(byOrg)[0] || [];

  connections = connections.filter(
    (c) =>
      !c.revoked_at &&
      (c.status === undefined || c.status === 'valid') &&
      campaignPlatforms.some((p) => matchesPlatform(c.platform, p)),
  );

  // Pre-enable defaults; if a platform has exactly one connection, enable it.
  const perPlatformCount = connections.reduce<Record<string, number>>((acc, c) => {
    acc[c.platform] = (acc[c.platform] ?? 0) + 1;
    return acc;
  }, {});

  return connections.map((c) => ({
    connectionId: c.id,
    platform: c.platform,
    accountId: c.platform_account_id || c.platform_user_id || 'default_account',
    accountName: c.account_name || 'Unnamed account',
    enabled: c.is_default || perPlatformCount[c.platform] === 1,
    isDefault: c.is_default,
  }));
}

export interface PageOption {
  id: string;
  name: string;
  isDefault: boolean;
}

/**
 * Facebook pages for a Meta connection (organic posts publish to a page).
 * Returns options with the backend's default flagged; empty on error so the
 * panel can show "no pages" instead of breaking.
 */
export async function loadFacebookPages(connectionId: string): Promise<PageOption[]> {
  try {
    const resp = await apiClient.get(`/api/v1/publisher/facebook-pages/${connectionId}`);
    return (resp.data?.pages ?? []).map((p: any) => ({
      id: p.page_id,
      name: p.page_name,
      isDefault: !!p.is_default,
    }));
  } catch {
    return [];
  }
}

/** Pinterest boards for a connection (pins publish to a board). */
export async function loadPinterestBoards(connectionId: string): Promise<PageOption[]> {
  try {
    const resp = await apiClient.get(`/api/v1/publisher/pinterest-boards/${connectionId}`);
    return (resp.data?.boards ?? []).map((b: any) => ({
      id: b.board_id,
      name: b.board_name,
      isDefault: !!b.is_default,
    }));
  } catch {
    return [];
  }
}

/**
 * Create a Pinterest board on the connection's account (same endpoint the
 * old modal used). Throws with the backend's detail message on failure so
 * the panel can surface it inline.
 */
export async function createPinterestBoard(
  connectionId: string,
  name: string,
): Promise<PageOption> {
  try {
    const resp = await apiClient.post('/api/v1/publisher/pinterest-boards', {
      connection_id: connectionId,
      name,
    });
    const board = resp.data ?? {};
    return {
      id: board.board_id,
      name: board.board_name ?? name,
      isDefault: !!board.is_default,
    };
  } catch (e: any) {
    throw new Error(
      e?.response?.data?.detail || e?.message || 'Failed to create board',
    );
  }
}

export interface PublishRequest {
  conversationId: string;
  userId: string;
  mode: 'paid' | 'organic';
  timing: { type: 'now' | 'scheduled'; at?: string | null };
  accounts: PublishAccount[];
  /** Concept ids picked for organic posting (launch.organicSelection) */
  organicSelection?: string[];
  campaignState: CampaignState;
  /** Demo campaign — publisher returns a fixed result, no real ad-platform call */
  isDemo?: boolean;
  /** Budget health at launch time — stored in publishing_metadata for analytics */
  budgetHealthSnapshot?: Record<string, { status: string; implications: string }>;
  /**
   * The conversation's uploaded images, exactly as the canvas previews receive them
   * (ChatInterface passes conversationMedia filtered to images). The previews fall back
   * to these positionally for a concept with no asset of its own, so publishing has to
   * see them too or it ships an ad with no image while the visualizer showed one.
   */
  uploadedImages?: Array<{ url: string }>;
}

/**
 * Map organic concept selection onto the campaign_config variation indices
 * the publisher consumes. Until the media pipeline links concepts to
 * variations explicitly, the mapping is positional over included concepts.
 */
function organicVariationIndices(state: CampaignState, selection: string[]): number[] {
  const included = (state.creativeSet ?? []).filter((c) => c.included !== false);
  return included
    .map((concept, index) => ({ concept, index }))
    .filter(({ concept }) => selection.includes(concept.id))
    .map(({ index }) => index);
}

export async function publishCampaign(request: PublishRequest): Promise<PublishOutcome> {
  const { conversationId, userId, mode, timing, accounts, campaignState, isDemo } = request;
  const enabled = accounts.filter((a) => a.enabled);
  if (enabled.length === 0) {
    return { ok: false, scheduled: false, results: [], error: 'No accounts selected' };
  }

  // The publisher consumes the CANONICAL proposal (per-platform campaigns[]
  // with ad_sets/ads) — the same shape the old modals passed. It lives on
  // the last assistant message's campaign_proposal; conversation.campaign_config
  // (ReviewPanel shape) is only a last-resort fallback.
  let campaignConfig: any = null;
  let campaignTitle = campaignState.name || `Campaign ${conversationId}`;
  try {
    const conversationResp = await apiClient.get(`/api/v1/campaigns/${conversationId}`);
    const messages: any[] = conversationResp.data?.messages ?? [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const proposal = messages[i]?.content?.campaign_proposal;
      if (proposal) {
        campaignConfig = proposal.canonical ?? proposal;
        break;
      }
    }
    campaignConfig =
      campaignConfig ??
      conversationResp.data?.campaign_proposal?.proposal ??
      conversationResp.data?.conversation?.campaign_config ??
      conversationResp.data?.campaign_config ??
      null;
    // Prefer the name the user sees on the canvas (campaignState.name) so the platform
    // campaign name matches Admeru; fall back to the proposal title only when unset.
    campaignTitle = campaignState.name || campaignConfig?.title || campaignTitle;
  } catch {
    // GET failed — fall through to canvas-state derivation below.
  }

  // Canvas-native flow: no stored campaign_config on the conversation.
  // Derive one from the current canvas creative set so the publisher can
  // extract ad creatives (headline, body, image_url).
  if (!campaignConfig) {
    campaignConfig = buildCampaignConfigFromState(
      campaignState,
      accounts,
      request.organicSelection ?? [],
      request.uploadedImages,
    );
  }

  // Compiled configs hard-code placement posture without knowing which canvas
  // renditions are enabled. Patch Meta ad set placement now so that a concept
  // with story_9x16 enabled also reaches story placements.
  if (campaignState.creativeSet?.length) {
    applyRenditionPlacements(campaignConfig, campaignState.creativeSet);
  }

  try {
    // ── Scheduled (paid or organic) ────────────────────────────────────
    if (timing.type === 'scheduled') {
      if (!timing.at) {
        return { ok: false, scheduled: true, results: [], error: 'No schedule time set' };
      }
      const payload: any = {
        conversation_id: conversationId,
        scheduled_at: new Date(timing.at).toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        publish_type: mode === 'paid' ? 'paid_ad' : 'organic_post',
        campaign_config: campaignConfig,
        campaign_title: campaignTitle,
        // pageId/boardId mirror the "organic, now" mapping below — without
        // them, a scheduled Meta publish silently falls back to the org's
        // default Page (ignoring what the user picked in the launch panel),
        // and a scheduled Pinterest publish fails outright (Pinterest requires
        // a real board_id, not the connection's own account id).
        connections: enabled.map((a) => {
          const conn: any = {
            connection_id: a.connectionId,
            account_id: a.accountId,
            platform: a.platform,
            account_name: a.accountName,
          };
          if (a.pageId) {
            conn.page_id = a.pageId;
            if (a.pageName) conn.page_name = a.pageName;
          }
          if (a.boardId) conn.board_id = a.boardId;
          return conn;
        }),
        user_id: userId,
      };
      if (mode === 'organic' && request.organicSelection?.length) {
        // Concept ids are authoritative (matched against stamped
        // variations); indices remain as the legacy positional fallback.
        payload.concept_ids = request.organicSelection;
        payload.variation_indices = organicVariationIndices(
          campaignState,
          request.organicSelection,
        );
      }
      const response = await apiClient.post('/api/v1/scheduler/schedule', payload);
      return { ok: true, scheduled: true, results: [response.data] };
    }

    // ── Organic, now ───────────────────────────────────────────────────
    if (mode === 'organic') {
      const selections = enabled.map((a) => {
        const sel: any = {
          platform: a.platform === 'meta' ? 'facebook' : a.platform,
          connection_id: a.connectionId,
          account_id: a.accountId,
          account_name: a.accountName,
        };
        if (a.pageId) {
          sel.page_id = a.pageId;
          if (a.pageName) sel.page_name = a.pageName;
        }
        if (a.boardId) sel.board_id = a.boardId;
        return sel;
      });
      const response = await apiClient.post('/api/v1/publisher/publish-organic', {
        conversation_id: conversationId,
        campaign_title: campaignTitle,
        campaign_config: campaignConfig,
        // Concept ids are authoritative (matched against stamped
        // variations); indices remain as the legacy positional fallback.
        selected_concept_ids: request.organicSelection ?? [],
        selected_variation_indices: organicVariationIndices(
          campaignState,
          request.organicSelection ?? [],
        ),
        selections,
        user_id: userId,
      });
      const results = response.data?.results ?? [];
      const failed = results.filter((r: any) => r.status === 'failed' || r.success === false);
      const ok = failed.length < results.length || results.length === 0;
      const errorDetail = !ok
        ? failed.map((r: any) => r.error || r.message || `${r.platform ?? 'platform'} failed`).join('; ')
        : undefined;
      return {
        ok,
        scheduled: false,
        status:
          failed.length === 0
            ? 'published'
            : failed.length < results.length
              ? 'partially_published'
              : 'publish_failed',
        results,
        error: errorDetail || undefined,
      };
    }

    // ── Paid, now ──────────────────────────────────────────────────────
    const selectedByPlatform: Record<
      string,
      Array<{ id: string; account_id: string; board_id?: string }>
    > = {};
    for (const a of enabled) {
      (selectedByPlatform[a.platform] ??= []).push({
        id: a.connectionId,
        account_id: a.accountId,
        ...(a.boardId ? { board_id: a.boardId } : {}),
        ...(a.pageId ? { page_id: a.pageId } : {}),
      });
    }
    const response = await apiClient.post('/api/v1/publisher/publish-multi', {
      conversation_id: conversationId,
      campaign_title: campaignTitle,
      campaign_config: campaignConfig,
      selected_connections: selectedByPlatform,
      user_id: userId,
      is_demo: isDemo ?? false,
      budget_health_snapshot: request.budgetHealthSnapshot ?? null,
    });
    const { successful = 0, failed = 0, results = [] } = response.data ?? {};
    const ok = successful > 0 || failed === 0;
    const errorDetail = !ok
      ? (results as any[])
          .filter((r) => r.error || r.message || r.status === 'failed')
          .map((r) => r.error || r.message || `${r.platform ?? 'platform'} failed`)
          .join('; ')
      : undefined;
    return {
      ok,
      scheduled: false,
      status:
        failed === 0 ? 'published' : successful > 0 ? 'partially_published' : 'publish_failed',
      results,
      error: errorDetail || undefined,
    };
  } catch (e: any) {
    return {
      ok: false,
      scheduled: timing.type === 'scheduled',
      results: [],
      error: extractErrorMessage(e, 'Publish failed'),
    };
  }
}
